// ---------------------------------------------------------------------------
// D2: Create Deal Modal
// ---------------------------------------------------------------------------
// Fields: deal name (required), value (€), stage (from pipeline), contact
// (search-as-you-type), expected close date, notes.
// POST /v1/deals. Toast on success. Design system applied.
// ---------------------------------------------------------------------------
import { useState, useEffect, useId, useRef, type FormEvent } from "react";
import { X, BadgeDollarSign, Search } from "lucide-react";
import { apiFetch } from "@/lib/api-client";
import type { PipelineStage } from "@/hooks/use-deals";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CreateDealData {
  title: string;
  value: number | null; // cents
  stageId: string;
  pipelineId: string;
  personId: string | null;
  expectedCloseDate: string | null;
  notes: string;
}

interface CreateDealModalProps {
  pipelineId: string;
  stages: PipelineStage[];
  onSubmit: (data: CreateDealData) => Promise<void>;
  onClose: () => void;
  isSubmitting?: boolean;
}

interface ContactResult {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CreateDealModal({
  pipelineId,
  stages,
  onSubmit,
  onClose,
  isSubmitting = false,
}: CreateDealModalProps) {
  const formId = useId();
  const [title, setTitle] = useState("");
  const [valueStr, setValueStr] = useState("");
  const [stageId, setStageId] = useState(stages[0]?.id ?? "");
  const [personId, setPersonId] = useState<string | null>(null);
  const [expectedCloseDate, setExpectedCloseDate] = useState("");
  const [notes, setNotes] = useState("");

  // Contact search state
  const [contactSearch, setContactSearch] = useState("");
  const [contactResults, setContactResults] = useState<ContactResult[]>([]);
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Validation
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [shakeField, setShakeField] = useState<string | null>(null);

  // Debounced contact search with abort on unmount/re-search
  useEffect(() => {
    if (contactSearch.length < 2) {
      setContactResults([]);
      setShowContactDropdown(false);
      return;
    }

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    const controller = new AbortController();

    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const res = await apiFetch<{ data: { items: ContactResult[] } }>(
          `/crm/people?search=${encodeURIComponent(contactSearch)}&limit=5`,
          { signal: controller.signal },
        );
        setContactResults(res.data.items);
        setShowContactDropdown(true);
      } catch {
        if (!controller.signal.aborted) {
          setContactResults([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsSearching(false);
        }
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      controller.abort();
    };
  }, [contactSearch]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowContactDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectContact = (contact: ContactResult) => {
    setPersonId(contact.id);
    const name = [contact.firstName, contact.lastName].filter(Boolean).join(" ") || contact.email || "Unknown";
    setContactSearch(name);
    setShowContactDropdown(false);
  };

  const clearContact = () => {
    setPersonId(null);
    setContactSearch("");
    setContactResults([]);
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!title.trim()) {
      newErrors.title = "Deal name is required";
    }
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      const firstKey = Object.keys(newErrors)[0]!;
      setShakeField(firstKey);
      setTimeout(() => setShakeField(null), 300);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate() || isSubmitting) return;

    const parsed = parseFloat(valueStr);
    const valueCents = valueStr && !Number.isNaN(parsed) ? Math.round(parsed * 100) : null;

    await onSubmit({
      title: title.trim(),
      value: valueCents,
      stageId,
      pipelineId,
      personId,
      expectedCloseDate: expectedCloseDate || null,
      notes: notes.trim(),
    });
  };

  const fieldClass = (name: string) =>
    `w-full rounded-lg border px-3 py-2 text-sm text-foreground bg-background transition-all
    focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20 focus:border-[var(--color-primary)]
    ${errors[name] ? "border-[var(--color-destructive)] focus:ring-[var(--color-destructive)]/20 focus:border-[var(--color-destructive)]" : "border-border"}
    ${shakeField === name ? "animate-[shake_200ms_ease-in-out]" : ""}`;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[4px] transition-opacity duration-250"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="w-full max-w-lg rounded-xl border border-border bg-background shadow-xl animate-in zoom-in-95 fade-in duration-250"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-border px-6 py-4">
            <div className="flex items-center gap-2">
              <BadgeDollarSign className="h-5 w-5 text-[var(--color-primary)]" />
              <h2 className="text-lg font-semibold text-foreground">Create Deal</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Form */}
          <form id={formId} onSubmit={handleSubmit} className="space-y-4 px-6 py-4">
            {/* Deal name (required) */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Deal Name <span className="text-[var(--color-destructive)]">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => { setTitle(e.target.value); setErrors((p) => { const n = { ...p }; delete n.title; return n; }); }}
                className={fieldClass("title")}
                placeholder="e.g. Website Redesign for Acme"
                autoFocus
              />
              {errors.title && (
                <p className="mt-1 text-xs text-[var(--color-destructive)] animate-in slide-in-from-top-1 duration-150">
                  {errors.title}
                </p>
              )}
            </div>

            {/* Value + Stage row */}
            <div className="grid grid-cols-2 gap-3">
              {/* Value (€) */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Value</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-medium">
                    &euro;
                  </span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={valueStr}
                    onChange={(e) => setValueStr(e.target.value)}
                    className={`${fieldClass("value")} pl-7`}
                    style={{ fontVariantNumeric: "tabular-nums" }}
                    placeholder="0.00"
                  />
                </div>
              </div>

              {/* Stage */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Stage</label>
                <select
                  value={stageId}
                  onChange={(e) => setStageId(e.target.value)}
                  className={fieldClass("stageId")}
                >
                  {stages.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Contact search */}
            <div ref={dropdownRef} className="relative">
              <label className="block text-sm font-medium text-foreground mb-1">Contact</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  value={contactSearch}
                  onChange={(e) => {
                    setContactSearch(e.target.value);
                    if (personId) clearContact();
                  }}
                  className={`${fieldClass("contact")} pl-9`}
                  placeholder="Search contacts..."
                />
                {personId && (
                  <button
                    type="button"
                    onClick={clearContact}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label="Clear contact"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              {/* Search results dropdown */}
              {showContactDropdown && (
                <div className="absolute z-10 mt-1 w-full rounded-lg border border-border bg-background shadow-lg max-h-48 overflow-y-auto">
                  {isSearching ? (
                    <div className="px-3 py-2 text-sm text-muted-foreground">Searching...</div>
                  ) : contactResults.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-muted-foreground">No contacts found</div>
                  ) : (
                    contactResults.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => selectContact(c)}
                        className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors text-left"
                      >
                        <span className="font-medium">
                          {[c.firstName, c.lastName].filter(Boolean).join(" ") || "Unnamed"}
                        </span>
                        {c.email && (
                          <span className="text-muted-foreground truncate">{c.email}</span>
                        )}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Expected close date */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Expected Close Date</label>
              <input
                type="date"
                value={expectedCloseDate}
                onChange={(e) => setExpectedCloseDate(e.target.value)}
                className={fieldClass("expectedCloseDate")}
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className={`${fieldClass("notes")} resize-none`}
                rows={3}
                placeholder="Any additional context..."
              />
            </div>
          </form>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 border-t border-border px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              form={formId}
              disabled={isSubmitting}
              className="btn-press rounded-lg px-5 py-2 text-sm font-medium text-white transition-colors disabled:opacity-50"
              style={{ background: "var(--gradient-energy)" }}
            >
              {isSubmitting ? "Creating..." : "Create Deal"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
